'use client';

import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, TextField, Typography } from '@mui/material';
import WebsiteButton from '@/components/website/WebsiteButton';
import { WEBSITE_PAGE_BACKGROUND, WEBSITE_PAGE_BACKGROUND_DARK } from '@/consts/website-theme';
import { WEBSITE_FOOTER_NEWSLETTER } from './website-footer-data';
import { isValidEmail } from '@/utils/required-fields';

const FOOTER_INPUT_TEXT = '#1a1a1a';
const FOOTER_INPUT_PLACEHOLDER = 'rgba(0, 0, 0, 0.48)';

export default function FooterNewsletterSignup({ sx }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async event => {
    event.preventDefault();
    const trimmed = email.trim();

    if (!isValidEmail(trimmed)) {
      setError('Enter a valid email address.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      const res = await fetch('/t/campaigns/public/newsletter-subscribers/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      let payload = null;
      try {
        payload = await res.json();
      } catch {
        payload = null;
      }

      if (!res.ok) {
        if (payload?.email) {
          const emailErr = Array.isArray(payload.email) ? payload.email[0] : payload.email;
          setError(String(emailErr));
        } else {
          setError('Something went wrong. Please try again.');
        }
        return;
      }

      if (payload?.detail === 'already_subscribed') {
        setFeedbackMessage(WEBSITE_FOOTER_NEWSLETTER.alreadySubscribedMessage);
      } else {
        setFeedbackMessage(WEBSITE_FOOTER_NEWSLETTER.successMessage);
      }
      setEmail('');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        ...sx,
      }}
    >
      <Typography
        component="h3"
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'text.primary',
          mb: 1,
        }}
      >
        {WEBSITE_FOOTER_NEWSLETTER.title}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          lineHeight: 1.65,
          color: 'text.secondary',
          mb: 2,
        }}
      >
        {WEBSITE_FOOTER_NEWSLETTER.description}
      </Typography>

      {feedbackMessage ? (
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{feedbackMessage}</Typography>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            alignItems: { sm: 'flex-start' },
          }}
        >
          <TextField
            type="email"
            name="email"
            value={email}
            onChange={event => {
              setEmail(event.target.value);
              if (error) setError('');
            }}
            placeholder={WEBSITE_FOOTER_NEWSLETTER.placeholder}
            autoComplete="email"
            size="small"
            fullWidth
            error={Boolean(error)}
            helperText={error}
            disabled={isSubmitting}
            FormHelperTextProps={{ sx: { mx: 0 } }}
            inputProps={{ 'aria-label': 'Email address for newsletter' }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                bgcolor: WEBSITE_PAGE_BACKGROUND,
                color: FOOTER_INPUT_TEXT,
                fontSize: 13,
                '& fieldset': {
                  borderColor: WEBSITE_PAGE_BACKGROUND_DARK,
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.38)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: FOOTER_INPUT_TEXT,
                },
              },
              '& .MuiInputBase-input': {
                color: FOOTER_INPUT_TEXT,
                '&::placeholder': {
                  color: FOOTER_INPUT_PLACEHOLDER,
                  opacity: 1,
                },
              },
            }}
          />
          <WebsiteButton
            type="submit"
            disabled={isSubmitting}
            sx={{ flexShrink: 0, px: 2.5, py: 0.95 }}
          >
            {WEBSITE_FOOTER_NEWSLETTER.buttonLabel}
          </WebsiteButton>
        </Box>
      )}
    </Box>
  );
}

FooterNewsletterSignup.propTypes = {
  sx: PropTypes.object,
};
